import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowRight, FileDown, CalendarPlus } from "lucide-react";
import { createPortal } from "react-dom";
import { getAssessment, SEVERITY_COLORS } from "@/lib/api";
import { ScoreDial } from "@/components/ScoreDial";
import { DimensionBars } from "@/components/DimensionBars";
import { FeedbackForm } from "@/components/FeedbackForm";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { LegalFooter } from "@/components/LegalFooter";
import { BrandLockup } from "@/components/BrandLockup";

const Section = ({ overline, title, children, testId }) => (
  <section className="border-t border-[#E2DFD8] pt-12" data-testid={testId}>
    {overline && (
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-4">{overline}</p>
    )}
    {title && (
      <h2 className="font-display text-2xl md:text-3xl font-normal tracking-tight text-[#0B0E14] mb-8">{title}</h2>
    )}
    {children}
  </section>
);

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [result, setResult] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Refetch localized result whenever the language changes.
  useEffect(() => {
    let active = true;
    setLoading(true);
    getAssessment(id, lang)
      .then((d) => { if (active) { setResult(d.result); setFeedback(d.feedback); setError(false); } })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, lang]);

  if (loading && !result) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#A68A56] animate-spin" />
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex flex-col items-center justify-center px-6 text-center">
        <p className="text-base text-[#4A5568]">{t.results.notFound}</p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 bg-[#0B0E14] text-white px-8 py-4 text-sm font-medium"
        >
          {t.results.startOver}
        </button>
      </div>
    );
  }

  const { index, severity, dimension_scores, primary_bottleneck, confidence, signals, diagnosis, strategic_move, interventions, plan, success_metric } = result;
  const color = SEVERITY_COLORS[severity.key];
  const r = t.results;
  const ip = r.interventionPlaybook[primary_bottleneck.key];

  const handlePrint = () => window.print();

  const downloadIcs = () => {
    const pad = (n) => String(n).padStart(2, "0");
    const toUtc = (d) =>
      `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
    const esc = (s) => String(s).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
    const start = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    start.setUTCHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 30 * 60000);
    const desc = `${ip.protocol} — ${success_metric.label}. ${r.launch.signature}`;
    const ics = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//KOS//Founder Bottleneck//EN", "CALSCALE:GREGORIAN",
      "BEGIN:VEVENT", `UID:${id}-d30@founderbottleneck`, `DTSTAMP:${toUtc(new Date())}`,
      `DTSTART:${toUtc(start)}`, `DTEND:${toUtc(end)}`,
      `SUMMARY:${esc(r.launch.reminderEventTitle)}`, `DESCRIPTION:${esc(desc)}`,
      "BEGIN:VALARM", "TRIGGER:-PT30M", "ACTION:DISPLAY", `DESCRIPTION:${esc(r.launch.reminderEventTitle)}`, "END:VALARM",
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "founder-bottleneck-day30.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex flex-col">
      <header className="border-b border-[#E2DFD8] bg-[#F6F5F2]/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <span data-testid="brand-header">
            <BrandLockup />
          </span>
          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate("/")}
              data-testid="home-btn"
              className="text-xs uppercase tracking-[0.18em] text-[#718096] hover:text-[#0B0E14] transition-colors"
            >
              {t.nav.home}
            </button>
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 md:px-12 py-16 md:py-24">
        <div className="flex flex-col items-center text-center" data-testid="results-index">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-8">
            {r.indexOverline}
          </p>
          <ScoreDial score={index} severityKey={severity.key} severityLabel={severity.label} unitLabel={r.of100} />
          <p className="text-base text-[#4A5568] mt-8 max-w-md">{r.indexHelp}</p>
        </div>

        <div className="mt-20">
          <Section overline={r.primaryOverline} testId="primary-bottleneck">
            <div className="flex items-center gap-4 mb-4">
              <span className="w-3 h-3" style={{ backgroundColor: color }} />
              <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-[#0B0E14]" data-testid="primary-bottleneck-name">
                {primary_bottleneck.name}
              </h2>
              <span className="font-display text-xl text-[#718096] tabular-nums">{primary_bottleneck.score}</span>
            </div>
            <p className="text-sm text-[#4A5568] mb-6 max-w-2xl" data-testid="confidence-statement">{confidence.statement}</p>
            <p className="font-display text-xl md:text-2xl font-light text-[#0B0E14] leading-relaxed max-w-2xl" data-testid="diagnosis-headline">
              {diagnosis.headline}
            </p>

            <div className="mt-8 bg-white border border-[#E2DFD8] p-6 max-w-2xl" data-testid="signals">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A68A56] mb-4">{r.signalsTitle}</p>
              <ul className="flex flex-col gap-3">
                {signals.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-base text-[#2C363F]">
                    <span className="w-1.5 h-1.5 mt-2 shrink-0" style={{ backgroundColor: color }} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </Section>
        </div>

        <div className="mt-16">
          <Section overline="" title={r.whyTitle} testId="why-bottleneck">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { label: r.symptomLabel, text: diagnosis.symptom },
                { label: r.mechanismLabel, text: diagnosis.mechanism },
                { label: r.consequenceLabel, text: diagnosis.consequence },
              ].map((b) => (
                <div key={b.label} className="bg-white border border-[#E2DFD8] p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A68A56] mb-4">{b.label}</p>
                  <p className="text-base text-[#2C363F] leading-relaxed">{b.text}</p>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div className="mt-20">
          <Section overline={r.fullPictureOverline} title={r.fullPictureTitle} testId="dimension-breakdown">
            <DimensionBars dimensions={dimension_scores} primaryKey={primary_bottleneck.key} primaryLabel={r.primaryTag} />
          </Section>
        </div>

        <div className="mt-20">
          <Section overline="" testId="strategic-move">
            <div className="bg-[#0B0E14] text-white p-8 md:p-12">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C9A96A] mb-4">{r.strategicMoveOverline}</p>
              <p className="font-display text-2xl md:text-3xl font-light leading-snug max-w-3xl" data-testid="strategic-move-text">
                {strategic_move}
              </p>
            </div>
          </Section>
        </div>

        <div className="mt-20" data-testid="what-next">
          <Section overline={r.launch.whatNextOverline} testId="what-next-section">
            <p className="font-display text-xl md:text-2xl font-light text-[#0B0E14] leading-relaxed max-w-2xl">
              {r.launch.whatNextBody}
            </p>

            {ip && (
              <div className="mt-10 bg-white border border-[#E2DFD8]" data-testid="first-intervention">
                <div className="p-6 md:p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-3">{r.launch.firstInterventionOverline}</p>
                  <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight text-[#0B0E14]" data-testid="intervention-protocol">
                    {ip.protocol}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E2DFD8] border-t border-[#E2DFD8]">
                  {[
                    { n: r.launch.q01, text: ip.change },
                    { n: r.launch.q02, text: ip.observe },
                    { n: r.launch.q03, text: ip.act },
                    { n: r.launch.q04, text: ip.measure },
                  ].map((b, i) => (
                    <div key={i} className="bg-white p-6 md:p-8" data-testid={`intervention-step-${i + 1}`}>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#A68A56] mb-3">{b.n}</p>
                      <p className="text-base text-[#2C363F] leading-relaxed">{b.text}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#E2DFD8] p-6 md:p-8 bg-[#F6F5F2]">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#718096] mb-2">{r.launch.centralQuestion}</p>
                  <p className="font-display text-lg md:text-xl font-light text-[#0B0E14] leading-snug italic" data-testid="intervention-question">
                    {ip.question}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-8 border border-[#A68A56]/40 bg-[#A68A56]/[0.06] p-6 md:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="start-journal-block">
              <div>
                <p className="font-display text-lg text-[#0B0E14]">{t.journal.overline}</p>
                <p className="text-sm text-[#718096] mt-1">{t.journal.startHint}</p>
              </div>
              <button
                onClick={() => navigate(`/journal/${id}`)}
                data-testid="start-journal"
                className="inline-flex items-center justify-center gap-2 bg-[#0B0E14] text-white px-6 py-3 text-sm font-medium hover:bg-[#1a2029] transition-colors whitespace-nowrap"
              >
                {t.journal.startCta}
                <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>

            <div className="mt-6 no-print" data-testid="share-bar">
              <button
                onClick={handlePrint}
                data-testid="download-pdf"
                className="inline-flex items-center justify-center gap-2 border border-[#E2DFD8] bg-white px-6 py-3 text-sm font-medium text-[#0B0E14] hover:border-[#A68A56] transition-colors"
              >
                <FileDown className="w-4 h-4" strokeWidth={1.75} />
                {r.launch.downloadPdf}
              </button>
            </div>
          </Section>
        </div>

        <div className="mt-20">
          <Section overline={r.startHereOverline} title={r.prioritiesTitle} testId="priorities">
            <div className="flex flex-col gap-px bg-[#E2DFD8] border border-[#E2DFD8]">
              {interventions.map((p, i) => (
                <div key={i} className="bg-white flex items-start gap-6 p-8" data-testid={`priority-${i + 1}`}>
                  <span className="font-display text-3xl font-light text-[#A68A56] leading-none tabular-nums">
                    {i + 1}
                  </span>
                  <p className="text-base md:text-lg text-[#0B0E14] leading-relaxed pt-1">{p}</p>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div className="mt-20">
          <Section overline={r.planOverline} title={r.planTitle} testId="plan">
            <div className="flex flex-col gap-6">
              {plan.map((w) => (
                <div key={w.week} className="bg-white border border-[#E2DFD8] p-8" data-testid={`plan-week-${w.week}`}>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-5">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A68A56]">{r.launch.phaseDays[w.phase_key]}</span>
                    <span className="text-xs uppercase tracking-[0.18em] text-[#718096]">· {r.phases[w.phase_key]}</span>
                    <span className="font-display text-lg font-medium text-[#0B0E14] w-full sm:w-auto">{w.title}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {[
                      { label: r.planFields.objective, text: w.objective },
                      { label: r.planFields.action, text: w.action },
                      { label: r.planFields.output, text: w.output },
                      { label: r.planFields.metric, text: w.metric },
                    ].map((f) => (
                      <div key={f.label}>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#718096] mb-1">{f.label}</p>
                        <p className="text-sm text-[#2C363F] leading-relaxed">{f.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div className="mt-20">
          <Section overline="" testId="success-metric">
            <div className="border-2 border-[#0B0E14] p-8 md:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A68A56] mb-4">{r.successOverline}</p>
              <p className="font-display text-xl md:text-2xl font-normal text-[#0B0E14] leading-snug" data-testid="success-metric-label">
                {success_metric.label}
              </p>
              <p className="text-base text-[#4A5568] leading-relaxed mt-3 max-w-2xl">{success_metric.description}</p>
              <p className="text-sm text-[#718096] leading-relaxed mt-4 max-w-2xl border-t border-[#E2DFD8] pt-4" data-testid="metric-clarifier">
                {r.launch.metricClarifier}
              </p>
              <div className="mt-6 no-print" data-testid="reminder-block">
                <button
                  onClick={downloadIcs}
                  data-testid="add-calendar-reminder"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#0B0E14] border-b border-[#A68A56] pb-0.5 hover:text-[#A68A56] transition-colors"
                >
                  <CalendarPlus className="w-4 h-4" strokeWidth={1.75} />
                  {r.launch.reminderTitle}
                </button>
                <p className="text-xs text-[#718096] mt-2 max-w-md">{r.launch.reminderHint}</p>
              </div>
            </div>
          </Section>
        </div>

        <div className="mt-20">
          <Section overline={t.feedback.overline} title={t.feedback.title} testId="feedback">
            <FeedbackForm assessmentId={id} existingFeedback={feedback} />
          </Section>
        </div>

        <div className="mt-16" data-testid="assist-cta">
          <div className="bg-white border border-[#E2DFD8] p-8 md:p-10">
            <p className="font-display text-lg md:text-xl font-normal text-[#0B0E14] leading-snug max-w-2xl">{r.assist.q}</p>
            <p className="text-base text-[#4A5568] mt-3 max-w-2xl">{r.assist.line}</p>
            <p className="text-base text-[#4A5568] mt-2 max-w-2xl">{r.assist.body}</p>
            <a
              href={`https://wa.me/50764952282?text=${encodeURIComponent(r.assist.waMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="assist-cta-link"
              className="inline-flex items-center gap-2 mt-6 bg-[#0B0E14] text-white px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-[#2C363F] transition-colors"
            >
              {r.assist.cta}
            </a>
          </div>
        </div>

        <div className="mt-20 border-t border-[#E2DFD8] pt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-base text-[#4A5568] max-w-md">{r.retakeText}</p>
          <button
            onClick={() => navigate("/diagnostic")}
            data-testid="retake-btn"
            className="group inline-flex items-center gap-3 border border-[#0B0E14] text-[#0B0E14] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#0B0E14] hover:text-white transition-colors"
          >
            {r.retakeCta}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.75} />
          </button>
        </div>

        <div className="mt-20 pt-8 border-t border-[#E2DFD8] flex justify-center" data-testid="report-signature">
          <p className="text-xs uppercase tracking-[0.3em] text-[#A68A56]">{r.launch.signature}</p>
        </div>

      </main>

      {createPortal(
        <div id="print-report-root" aria-hidden="true">
          <div className="pdf">
            <section className="pdf-cover">
              <div className="pdf-brand"><BrandLockup /></div>
              <div className="pdf-cover-body">
                <p className="pdf-overline">Founder Dependency Index™</p>
                <p className="pdf-index">{index}<span className="pdf-index-unit">/100</span></p>
                <p className="pdf-severity">{severity.label}</p>
                <p className="pdf-cover-title">{primary_bottleneck.name}</p>
              </div>
              <p className="pdf-cover-foot">{r.launch.signature}</p>
            </section>

            <section className="pdf-section">
              <h2 className="pdf-h">01 · {r.primaryOverline}</h2>
              <div className="pdf-row">
                <span className="pdf-k">{primary_bottleneck.name}</span>
                <span className="pdf-score">{primary_bottleneck.score}</span>
              </div>
              <p className="pdf-sev">{severity.label}</p>
              <p className="pdf-p">{confidence.statement}</p>
              <p className="pdf-lead">{diagnosis.headline}</p>
            </section>

            <section className="pdf-section">
              <h2 className="pdf-h">02 · {r.whyTitle}</h2>
              {[
                [r.symptomLabel, diagnosis.symptom],
                [r.mechanismLabel, diagnosis.mechanism],
                [r.consequenceLabel, diagnosis.consequence],
              ].map(([label, text]) => (
                <div key={label} className="pdf-block">
                  <p className="pdf-label">{label}</p>
                  <p className="pdf-p">{text}</p>
                </div>
              ))}
            </section>

            <section className="pdf-section">
              <h2 className="pdf-h">03 · {r.strategicMoveOverline}</h2>
              <p className="pdf-lead">{strategic_move}</p>
            </section>

            <section className="pdf-section">
              <h2 className="pdf-h">04 · {r.launch.firstInterventionOverline}</h2>
              <p className="pdf-proto">{ip.protocol}</p>
              {[
                [r.launch.q01, ip.change],
                [r.launch.q02, ip.observe],
                [r.launch.q03, ip.act],
                [r.launch.q04, ip.measure],
              ].map(([label, text]) => (
                <div key={label} className="pdf-block">
                  <p className="pdf-label">{label}</p>
                  <p className="pdf-p">{text}</p>
                </div>
              ))}
              <div className="pdf-quote">
                <p className="pdf-label">{r.launch.centralQuestion}</p>
                <p className="pdf-lead">{ip.question}</p>
              </div>
            </section>

            <section className="pdf-section">
              <h2 className="pdf-h">05 · {r.planTitle}</h2>
              {plan.map((w) => (
                <div key={w.week} className="pdf-block">
                  <p className="pdf-label">{r.launch.phaseDays[w.phase_key]} · {r.phases[w.phase_key]} — {w.title}</p>
                  <div className="pdf-grid">
                    {[
                      [r.planFields.objective, w.objective],
                      [r.planFields.action, w.action],
                      [r.planFields.output, w.output],
                      [r.planFields.metric, w.metric],
                    ].map(([label, text]) => (
                      <div key={label}>
                        <p className="pdf-sublabel">{label}</p>
                        <p className="pdf-psm">{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>

            <section className="pdf-section">
              <h2 className="pdf-h">06 · {r.successOverline}</h2>
              <p className="pdf-proto">{success_metric.label}</p>
              <p className="pdf-p">{success_metric.description}</p>
            </section>

            <section className="pdf-close">
              <div className="pdf-brand" style={{ display: "inline-block" }}><BrandLockup /></div>
              <p className="pdf-tag">{r.launch.pdfTagline}</p>
            </section>
          </div>
        </div>,
        document.body
      )}

      <LegalFooter />
    </div>
  );
}
